import { useEffect, useRef, useState } from "react";
import { Search, Bell, Command as CmdIcon, User, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getNotifications, updateNotification } from "@/pages/lead-engine/api";

// How often to refresh the unread badge while the dropdown is closed - the
// dropdown's own contents are always refetched fresh on open (see
// handleToggle below), this is just for the badge count staying current
// without the user having to open it.
const POLL_INTERVAL_MS = 30000;

function timeAgo(iso) {
  if (!iso) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function TopBar({ onOpenCmd }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const refresh = async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.items || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    }
  };

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await refresh();
      setLoading(false);
    }
  };

  // Clicking a notification marks it read - it stays in the list (still
  // visible as history) until explicitly dismissed via its own "x".
  const handleMarkRead = async (runId) => {
    setNotifications((prev) => prev.map((n) => (n.id === runId ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await updateNotification(runId, { isRead: true });
    } catch (err) {
      console.error("Failed to mark notification read:", err);
      refresh(); // resync on failure rather than leave optimistic state wrong
    }
  };

  // The "x" button - removes it from the list entirely (dismissed=true,
  // never shown again), independent of whether it was read yet.
  const handleDismiss = async (e, runId, wasUnread) => {
    e.stopPropagation(); // don't also trigger the row's mark-as-read click
    setNotifications((prev) => prev.filter((n) => n.id !== runId));
    if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await updateNotification(runId, { dismissed: true });
    } catch (err) {
      console.error("Failed to dismiss notification:", err);
      refresh();
    }
  };

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#09090b]/85 backdrop-blur-xl"
    >
      <div className="flex items-center gap-3 px-6 lg:px-10 h-16">
        {/* Search / cmd trigger */}
        <button
          data-testid="topbar-cmd-trigger"
          onClick={onOpenCmd}
          className="flex items-center gap-2 flex-1 max-w-md rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-left text-sm text-white/50 hover:border-white/[0.14] hover:bg-white/[0.04] transition-all"
        >
          <Search className="h-4 w-4 text-white/40" />
          <span className="flex-1">Search jobs, leads, RFPs, companies…</span>
          <kbd className="hidden md:inline-flex items-center gap-1 rounded border border-white/[0.08] bg-white/[0.03] px-1.5 py-0.5 font-mono text-[10px] text-white/50">
            <CmdIcon className="h-3 w-3" /> K
          </kbd>
        </button>

        <div className="flex-1" />

        <div className="hidden md:flex items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.02] px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_#00e5ff]" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-white/60">
            Live
          </span>
        </div>

        <div className="relative" ref={panelRef}>
          <Button
            data-testid="topbar-notifications"
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            className="relative rounded-full text-white/70 hover:text-white hover:bg-white/[0.05]"
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-indigo-400" />
            )}
          </Button>

          {open && (
            <div
              data-testid="topbar-notifications-panel"
              className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-white/10 bg-[#0b0b0f] shadow-2xl shadow-black/40"
            >
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                  Notifications{unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
                </span>
              </div>

              {loading ? (
                <div className="px-4 py-8 text-center text-sm text-white/40">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-white/40">
                  No workflow failures right now.
                </div>
              ) : (
                <div className="divide-y divide-white/[0.06]">
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      data-testid={`notification-${n.id}`}
                      onClick={() => !n.is_read && handleMarkRead(n.id)}
                      className={`group relative flex items-start gap-2.5 px-4 py-3 pr-9 text-sm transition-colors cursor-pointer ${
                        n.is_read ? "bg-transparent hover:bg-white/[0.02]" : "bg-indigo-500/[0.06] hover:bg-indigo-500/[0.1]"
                      }`}
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {!n.is_read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />}
                          <span className="truncate text-[13px] font-medium text-white">
                            {n.input?.Company_list?.[0]?.company_name || "Workflow failed"}
                          </span>
                        </div>
                        <div className="mt-0.5 line-clamp-2 text-[12px] text-white/60">
                          {n.error || "The workflow did not complete successfully."}
                        </div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/35">
                          {timeAgo(n.completed_at || n.started_at)}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`notification-dismiss-${n.id}`}
                        onClick={(e) => handleDismiss(e, n.id, !n.is_read)}
                        className="absolute right-2.5 top-2.5 rounded-full p-1 text-white/30 opacity-0 transition-opacity hover:bg-white/10 hover:text-white/70 group-hover:opacity-100"
                        title="Dismiss"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          data-testid="topbar-user"
          className="flex items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.02] pl-1 pr-3 py-1 hover:border-white/[0.14] transition-colors cursor-pointer"
        >
          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center">
            <User className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
          </div>
          <div className="hidden md:block leading-tight">
            <div className="text-[12px] text-white">Base2Brand</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">
              Admin
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
